import { api } from '@packages/backend/convex/_generated/api';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from 'convex-helpers/react';
import { CheckCircle } from 'lucide-react';
import { ErrorAlert } from '@/components/ErrorAlert';
import { TimeDisplay } from '@/components/TimeDisplay';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ActionButton } from '@/components/ui+/ActionButton';
import { ColorBadge } from '@/components/ui+/ColorBadge';
import { CopyTextButton } from '@/components/ui+/CopyTextButton';
import { LoadingIcon } from '@/components/ui+/LoadingIcon';
import { LogJson } from '@/components/ui+/LogJson';

export const Route = createFileRoute('/a/linkly/requests/$id')({
  component: Component,
});

function Component() {
  const { id } = Route.useParams();

  // Requests
  const {
    data: request,
    error,
    isError,
    isPending: requestPending,
  } = useQuery(api.linklyRequests.getById, { linklyRequestId: id });

  // Get session to find linklyDeviceId
  const { data: session, isPending: sessionPending } = useQuery(
    api.linklySessions.getBySessionUuid,
    request ? { sessionUuid: request.sessionUuid } : 'skip',
  );

  // Responses
  const { data: responses } = useQuery(
    api.linklyResponses.getBySession,
    request ? { sessionUuid: request.sessionUuid } : 'skip',
  );

  if (requestPending || sessionPending) return <LoadingIcon />;
  if (isError) {
    return <ErrorAlert message={error.message} title="Error loading request details" />;
  }
  if (!request) return <div>Request not found.</div>;

  return (
    <div>
      <div className="mb-4">
        {session?.linklyDeviceId && (
          <ActionButton variant="outline" size="sm">
            <Link to="/a/linkly/devices/$id" params={{ id: session.linklyDeviceId }}>
              Back to Device
            </Link>
          </ActionButton>
        )}
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Request</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell className="font-medium">Request ID</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm">{request._id}</span>
                    <CopyTextButton text={request._id} />
                  </div>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium w-[150px]">Request</TableCell>
                <TableCell>
                  <ColorBadge color="blue">{request.requestType}</ColorBadge>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium">Session UUID</TableCell>
                <TableCell className="font-mono text-sm">{request.sessionUuid}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium">Created</TableCell>
                <TableCell>
                  <TimeDisplay timestamp={request._creationTime} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium">URL</TableCell>
                <TableCell className="break-all text-sm">{request.url}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium">Sent</TableCell>
                <TableCell>
                  {request.acceptedAt ? (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger>
                          <CheckCircle className="h-5 w-5 text-green-500" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>
                            Accepted at:{' '}
                            <TimeDisplay timestamp={request.acceptedAt} primaryFormat="HH:mm:ss" />
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ) : (
                    <span className="text-muted-foreground">Not sent</span>
                  )}
                </TableCell>
              </TableRow>
              {request.runError && (
                <TableRow>
                  <TableCell className="font-medium">Run Error</TableCell>
                  <TableCell className="text-red-500">{request.runError}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <div className="mt-4">
            <div className="font-medium mb-2">Request Body:</div>
            <LogJson jsonData={request.body} requireDebugParam={false} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Responses ({responses?.length || 0})</CardTitle>
        </CardHeader>
        <CardContent>
          {responses && responses.length > 0 ? (
            responses.map((response, index) => (
              <div key={response._id} className="mb-6 last:mb-0">
                <div className="flex items-center gap-2 mb-2">
                  <div className="font-medium">
                    Response {responses.length > 1 ? `${index + 1}` : ''}
                  </div>
                  {response.responseType && (
                    <ColorBadge color="green">{response.responseType}</ColorBadge>
                  )}
                  {response.parseError && <ColorBadge color="red">Parse Error</ColorBadge>}
                  <div className="text-sm text-muted-foreground ml-auto">
                    <TimeDisplay timestamp={response._creationTime} primaryFormat="hh:mm:ss:s" />
                  </div>
                </div>

                <LogJson jsonData={response.rawData} requireDebugParam={false} />

                {response.parseError && (
                  <div className="mt-2">
                    <div className="text-red-500 font-medium mb-1">Parse Error:</div>
                    <div className="text-red-500 bg-red-50 p-2 rounded text-sm">
                      {response.parseError}
                    </div>
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="text-center py-4 text-muted-foreground">No responses received yet</div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
